import {
  localeFromProfileLanguage,
  normalizePortalLocale,
  type PortalLocale,
} from '../config/languages';

export type { PortalLocale };

export const GLOBAL_LANGUAGE_KEY = 'language';
export const LANGUAGE_CHANGE_EVENT = 'languageChange';

const USER_LANGUAGE_KEY_PREFIX = 'linkPortalLanguage';

function canUseLocalStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

export function localeFromPrimaryLanguage(primaryLanguage: unknown): PortalLocale {
  return localeFromProfileLanguage(primaryLanguage);
}

export function userLanguageStorageKey(userId: number | string): string {
  return `${USER_LANGUAGE_KEY_PREFIX}:${userId}`;
}

export function getStoredPortalLanguage(userId?: number | string | null): PortalLocale {
  if (!canUseLocalStorage()) return 'en';

  if (userId !== null && userId !== undefined) {
    const userLanguage = window.localStorage.getItem(userLanguageStorageKey(userId));
    if (userLanguage) return normalizePortalLocale(userLanguage);
  }

  return normalizePortalLocale(window.localStorage.getItem(GLOBAL_LANGUAGE_KEY));
}

export function setStoredPortalLanguage(locale: unknown, userId?: number | string | null): PortalLocale {
  const normalizedLocale = normalizePortalLocale(locale);
  if (!canUseLocalStorage()) return normalizedLocale;

  window.localStorage.setItem(GLOBAL_LANGUAGE_KEY, normalizedLocale);
  if (userId !== null && userId !== undefined) {
    window.localStorage.setItem(userLanguageStorageKey(userId), normalizedLocale);
  }

  return normalizedLocale;
}

export function initializeUserPortalLanguage(
  userId: number | string,
  primaryLanguage: unknown
): PortalLocale {
  if (!canUseLocalStorage()) return localeFromPrimaryLanguage(primaryLanguage);

  const key = userLanguageStorageKey(userId);
  const existingUserLanguage = window.localStorage.getItem(key);
  const nextLocale = existingUserLanguage
    ? normalizePortalLocale(existingUserLanguage)
    : localeFromPrimaryLanguage(primaryLanguage);

  window.localStorage.setItem(key, nextLocale);
  window.localStorage.setItem(GLOBAL_LANGUAGE_KEY, nextLocale);
  return nextLocale;
}

export function notifyPortalLanguageChange(): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(LANGUAGE_CHANGE_EVENT));
  }
}
